import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiExternalLink, FiCopy, FiCalendar, FiArrowRight, FiCheckCircle, FiCheck, FiRefreshCw, FiSearch, FiImage, FiX, FiClock, FiChevronDown } from 'react-icons/fi';
import SeguimientoInlineEditor from './SeguimientoInlineEditor';
import { format, differenceInCalendarDays, parseISO, startOfWeek, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { useAuth } from '../../../hooks/useAuth';
import { subscribeToDailyTracking, saveDailyTracking, getWeekTracking, normalizeTiposMensaje, setCourseWorkspace } from '../../../services/courseService';

// --- Normalize old doneIds (bare courseId → courseId::recordatorio) ---
const normalizeDoneIds = (rawIds) => {
    return rawIds.map(id => id.includes('::') ? id : `${id}::recordatorio`);
};

// --- Weekly history from Firestore data ---
const getWeekHistory = (totalTrackable, weekData) => {
    const today = new Date();
    const weekStart = startOfWeek(today, { weekStartsOn: 1 });
    const days = [];
    for (let i = 0; i < 7; i++) {
        const d = addDays(weekStart, i);
        const dateStr = format(d, 'yyyy-MM-dd');
        const count = (weekData[dateStr] || []).length;
        const isToday = dateStr === format(today, 'yyyy-MM-dd');
        const isFuture = d > today;
        days.push({
            label: format(d, 'EEE', { locale: es }).toUpperCase(),
            fullLabel: format(d, "EEEE d 'de' MMMM", { locale: es }),
            date: format(d, 'd'),
            dateKey: dateStr,
            count,
            isToday,
            isFuture,
            pct: totalTrackable > 0 ? Math.round((count / totalTrackable) * 100) : 0
        });
    }
    return days;
};

// --- Compute next session from fechasSesiones array ---
const computeNextSession = (seg) => {
    const rawFechas = seg.fechasSesiones || [];
    // Normalize: support both string[] (legacy) and object[] (new format)
    const fechas = rawFechas.map(f => typeof f === 'string' ? { fecha: f, grupoId: null } : f);

    if (fechas.length === 0 && seg.proximaSesion) {
        return {
            proximaSesion: seg.proximaSesion,
            sesionActual: seg.sesionActual || '',
            totalSesiones: seg.totalSesiones || '',
            grupoId: null
        };
    }

    if (fechas.length === 0) {
        return { proximaSesion: null, sesionActual: '', totalSesiones: '', grupoId: null };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sorted = [...fechas].sort((a, b) => a.fecha.localeCompare(b.fecha));
    const nextIdx = sorted.findIndex(f => differenceInCalendarDays(parseISO(f.fecha), today) >= 0);

    if (nextIdx >= 0) {
        return {
            proximaSesion: sorted[nextIdx].fecha,
            sesionActual: String(nextIdx + 1),
            totalSesiones: String(sorted.length),
            grupoId: sorted[nextIdx].grupoId
        };
    }

    return {
        proximaSesion: null,
        sesionActual: String(sorted.length),
        totalSesiones: String(sorted.length),
        grupoId: null
    };
};

// --- Helpers ---
const getDiffLabel = (days) => {
    if (days === 0) return 'HOY';
    if (days === 1) return 'Mañana';
    if (days < 0) return `Hace ${Math.abs(days)} día(s)`;
    return `Faltan ${days} días`;
};

const getDiffBadge = (days) => {
    if (days === 0) return 'bg-red-100 text-red-700 border-red-200';
    if (days === 1) return 'bg-orange-100 text-orange-700 border-orange-200';
    if (days <= 3) return 'bg-amber-100 text-amber-700 border-amber-200';
    if (days > 3) return 'bg-green-100 text-green-700 border-green-200';
    return 'bg-gray-100 text-gray-600 border-gray-200';
};

const SeguimientoDiario = ({ courses }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [doneIds, setDoneIds] = useState(new Set());
    const [weekData, setWeekData] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDay, setSelectedDay] = useState(null);
    const [loadingTracking, setLoadingTracking] = useState(true);
    const [historyView, setHistoryView] = useState('semanal');
    const [expandedCourseId, setExpandedCourseId] = useState(null);
    const [monthData, setMonthData] = useState({});

    const todayKey = format(new Date(), 'yyyy-MM-dd');

    // Resolve workspace ID properly via setCourseWorkspace (async)
    const [wsId, setWsId] = useState(null);
    useEffect(() => {
        if (!user?.uid) return;
        setCourseWorkspace(user.uid).then(resolved => setWsId(resolved));
    }, [user?.uid]);

    // Subscribe to today's tracking from Firestore (real-time, workspace-shared)
    useEffect(() => {
        if (!user?.uid || !wsId) return;
        setLoadingTracking(true);
        const unsubscribe = subscribeToDailyTracking(user.uid, todayKey, (ids) => {
            setDoneIds(new Set(normalizeDoneIds(ids)));
            setLoadingTracking(false);
        }, wsId);
        return () => unsubscribe();
    }, [user?.uid, wsId, todayKey]);

    // Load weekly tracking data from Firestore
    useEffect(() => {
        if (!user?.uid || !wsId) return;
        const today = new Date();
        const weekStart = startOfWeek(today, { weekStartsOn: 1 });
        const dateKeys = [];
        for (let i = 0; i < 7; i++) {
            dateKeys.push(format(addDays(weekStart, i), 'yyyy-MM-dd'));
        }
        getWeekTracking(user.uid, dateKeys, wsId).then(data => setWeekData(data));
    }, [user?.uid, wsId, doneIds]);

    // Load monthly tracking data from Firestore
    useEffect(() => {
        if (!user?.uid || !wsId || historyView !== 'mensual') return;
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const dateKeys = [];
        for (let i = 1; i <= daysInMonth; i++) {
            dateKeys.push(format(new Date(year, month, i), 'yyyy-MM-dd'));
        }
        getWeekTracking(user.uid, dateKeys, wsId).then(data => setMonthData(data));
    }, [user?.uid, wsId, historyView, doneIds]);

    const toggleDone = useCallback((trackId) => {
        if (!wsId) { toast.error('Workspace no cargado, recarga la página'); return; }
        setDoneIds(prev => {
            const next = new Set(prev);
            if (next.has(trackId)) next.delete(trackId);
            else next.add(trackId);
            saveDailyTracking(user.uid, todayKey, [...next], wsId);
            return next;
        });
    }, [user?.uid, wsId, todayKey]);

    const resetAll = useCallback(() => {
        if (!wsId) return;
        saveDailyTracking(user.uid, todayKey, [], wsId);
        setDoneIds(new Set());
        toast.success('Progreso reiniciado');
    }, [user?.uid, wsId, todayKey]);

    // Build course groups with computed data and enabled message types
    const { allGroups, inactivosCount, stats, totalTrackable } = useMemo(() => {
        const activos = courses.filter(c => (c.seguimiento?.estado || 'activo') !== 'inactivo');
        const inactivosCount = courses.length - activos.length;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const allGroups = activos.map(course => {
            const seg = course.seguimiento || {};
            const computed = computeNextSession(seg);
            let diff = undefined;
            if (computed.proximaSesion) {
                diff = differenceInCalendarDays(parseISO(computed.proximaSesion), today);
            }
            const tipos = normalizeTiposMensaje(seg);
            const enabledTipos = tipos.filter(t => t.habilitado);
            return {
                ...course,
                _diffDays: diff,
                _computedProximaSesion: computed.proximaSesion,
                _computedSesionActual: computed.sesionActual,
                _computedTotalSesiones: computed.totalSesiones,
                _computedGrupoId: computed.grupoId,
                _enabledTipos: enabledTipos
            };
        });

        allGroups.sort((a, b) => {
            const da = a._diffDays;
            const db = b._diffDays;
            if (da === undefined && db === undefined) return 0;
            if (da === undefined) return 1;
            if (db === undefined) return -1;
            return da - db;
        });

        let totalTrackable = 0;
        allGroups.forEach(c => { totalTrackable += c._enabledTipos.length; });

        const stats = {
            total: activos.length,
            hoy: allGroups.filter(c => c._diffDays === 0).length,
            proximos3: allGroups.filter(c => c._diffDays !== undefined && c._diffDays >= 1 && c._diffDays <= 3).length
        };

        return { allGroups, inactivosCount, stats, totalTrackable };
    }, [courses]);

    // Helper: is a course fully done (all enabled tipos marked)?
    const isCourseFullyDone = (course) => {
        return course._enabledTipos.every(t => doneIds.has(`${course.id}::${t.id}`));
    };

    // Helper: count of done tipos for a course
    const courseDoneCount = (course) => {
        return course._enabledTipos.filter(t => doneIds.has(`${course.id}::${t.id}`)).length;
    };

    // Split into pending and done
    const allPending = allGroups.filter(c => !isCourseFullyDone(c));
    const allDone = allGroups.filter(c => isCourseFullyDone(c));
    const totalDoneItems = allGroups.reduce((acc, c) => acc + courseDoneCount(c), 0);
    const progress = totalTrackable > 0 ? Math.round((totalDoneItems / totalTrackable) * 100) : 0;

    // Filter by search
    const term = searchTerm.toLowerCase().trim();
    const matchSearch = (c) => !term || c.title?.toLowerCase().includes(term);
    const pending = allPending.filter(matchSearch);
    const done = allDone.filter(matchSearch);

    const buildVars = (course) => {
        const vars = course.defaultVariables || {};
        const mat = course.materials || {};
        const seg = course.seguimiento || {};
        const diff = course._diffDays;

        // Resolve WhatsApp group: use the group assigned to the next session
        const gruposWhatsapp = seg.gruposWhatsapp || [];
        let grupoEnlace = seg.grupoWhatsapp || ''; // legacy fallback
        if (gruposWhatsapp.length > 0) {
            const nextGrupoId = course._computedGrupoId;
            const matchedGroup = nextGrupoId ? gruposWhatsapp.find(g => g.id === nextGrupoId) : null;
            grupoEnlace = matchedGroup ? matchedGroup.enlace : (gruposWhatsapp[0]?.enlace || '');
        }

        // Dynamic label: "Falta 1 día" / "Faltan 3 días" / "HOY" / "Hace 2 día(s)"
        let diasRestantesLabel = '';
        if (diff !== undefined) {
            if (diff === 0) diasRestantesLabel = 'HOY es la Masterclass';
            else if (diff === 1) diasRestantesLabel = 'Falta 1 día';
            else if (diff > 1) diasRestantesLabel = `Faltan ${diff} días`;
            else diasRestantesLabel = `Hace ${Math.abs(diff)} día(s)`;
        }

        return {
            ...vars,
            capturasLink: mat.capturasLink || '',
            waLink: mat.waLink || '',
            grupoWhatsapp: grupoEnlace,
            diasRestantes: diff !== undefined ? String(Math.max(0, diff)).padStart(2, '0') : '',
            diasRestantesLabel,
            proximaSesionFecha: course._computedProximaSesion
                ? format(parseISO(course._computedProximaSesion), "EEEE d 'de' MMMM", { locale: es }).toUpperCase()
                : '',
            sesionActual: course._computedSesionActual || '',
            totalSesiones: course._computedTotalSesiones || ''
        };
    };

    const replaceVars = (template, allVars) => {
        if (!template) return '';
        let text = template;
        Object.entries(allVars).forEach(([key, val]) => {
            text = text.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), val || '');
        });
        return text;
    };

    const copyTypedMessage = (course, tipo) => {
        const allVars = buildVars(course);
        const msg = replaceVars(tipo.plantilla, allVars);

        if (!msg.trim()) {
            toast.error(`Plantilla vacía para "${tipo.nombre}" - configúrala en el curso primero`);
            return;
        }
        navigator.clipboard.writeText(msg);
        toast.success(`${tipo.nombre} de "${course.title}" copiado`);
    };

    const GroupRow = ({ course, isFullyDone }) => {
        const seg = course.seguimiento || {};
        const vars = course.defaultVariables || {};
        const diff = course._diffDays;
        const isToday = diff === 0;
        const gruposWa = seg.gruposWhatsapp || [];
        const hasWaGroup = gruposWa.length > 0 ? gruposWa.some(g => !!g.enlace) : !!seg.grupoWhatsapp;
        const hasImage = !!seg.imagenSeguimiento;
        const nextGrupo = course._computedGrupoId ? gruposWa.find(g => g.id === course._computedGrupoId) : gruposWa[0];
        const groupName = nextGrupo?.nombre || seg.nombreGrupo || '';
        const enabledTipos = course._enabledTipos;
        const singleType = enabledTipos.length <= 1;
        const tipo0 = enabledTipos[0];
        const trackId0 = tipo0 ? `${course.id}::${tipo0.id}` : null;
        const hasMessage0 = tipo0 && !!tipo0.plantilla;

        return (
            <div className={`rounded-xl border transition-all ${isFullyDone
                ? 'bg-gray-50 border-gray-100 opacity-60'
                : isToday
                    ? 'bg-red-50 border-red-200 shadow-sm'
                    : 'bg-white border-gray-200 hover:shadow-sm'
                }`}>
                {/* Course header row */}
                <div className="flex items-center gap-3 p-3">
                    {/* Checkbox - for single type, toggles the tipo; for multi, visual indicator */}
                    {singleType && trackId0 ? (
                        <button
                            onClick={() => toggleDone(trackId0)}
                            className={`w-8 h-8 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${doneIds.has(trackId0)
                                ? 'bg-green-500 border-green-500 text-white'
                                : 'border-gray-300 hover:border-green-400 text-transparent hover:text-green-300'
                                }`}
                        >
                            <FiCheck size={16} strokeWidth={3} />
                        </button>
                    ) : (
                        <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center shrink-0 text-[10px] font-bold ${isFullyDone
                            ? 'bg-green-500 border-green-500 text-white'
                            : 'border-gray-300 text-gray-400'
                            }`}>
                            {isFullyDone ? <FiCheck size={16} strokeWidth={3} /> : `${courseDoneCount(course)}/${enabledTipos.length}`}
                        </div>
                    )}

                    {/* Course avatar */}
                    {hasImage ? (
                        <a href={seg.imagenSeguimiento} target="_blank" rel="noopener noreferrer" title="Ver poster" className="shrink-0">
                            <img src={seg.imagenSeguimiento} alt="" className="w-9 h-9 rounded-lg object-cover border border-gray-200 hover:opacity-80 transition-opacity" />
                        </a>
                    ) : (
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-bold text-sm ${isToday && !isFullyDone ? 'bg-red-100 text-red-600' : 'bg-indigo-100 text-indigo-600'}`}>
                            {course.title?.charAt(0).toUpperCase()}
                        </div>
                    )}

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <h3 className={`font-bold text-sm truncate ${isFullyDone ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                                {course.title}
                            </h3>
                            {diff !== undefined && (
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border whitespace-nowrap ${getDiffBadge(diff)}`}>
                                    {getDiffLabel(diff)}
                                </span>
                            )}
                        </div>
                        {groupName && (
                            <p className="text-[11px] text-green-600 font-medium truncate">
                                {gruposWa.length > 1 && <span className="text-[10px] bg-green-100 text-green-700 px-1 py-0.5 rounded mr-1 font-bold">{gruposWa.length} grupos</span>}
                                {groupName}
                            </p>
                        )}
                        <div className="flex items-center gap-2 text-[11px] text-gray-400">
                            {course._computedSesionActual && (
                                <span>MC {course._computedSesionActual}{course._computedTotalSesiones ? `/${course._computedTotalSesiones}` : ''}</span>
                            )}
                            {vars.time && <span>• {vars.time}</span>}
                            {course._computedProximaSesion && (
                                <span>• {format(parseISO(course._computedProximaSesion), "EEE d MMM", { locale: es })}</span>
                            )}
                            {!hasWaGroup && <span className="text-amber-500 font-semibold">• Sin grupo WA</span>}
                            {enabledTipos.length === 0 && <span className="text-amber-500 font-semibold">• Sin mensaje</span>}
                        </div>
                    </div>

                    {/* Actions for single-type (same as before) */}
                    {singleType && (
                        <div className="flex items-center gap-1.5 shrink-0">
                            {hasImage && (
                                <a href={seg.imagenSeguimiento} target="_blank" rel="noopener noreferrer" title="Ver/descargar poster"
                                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-colors">
                                    <FiImage size={12} />
                                </a>
                            )}
                            {hasMessage0 && (
                                <button onClick={() => copyTypedMessage(course, tipo0)} title="Copiar mensaje"
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${isToday && !isFullyDone
                                        ? 'text-red-700 bg-red-100 hover:bg-red-200 border-red-200'
                                        : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border-emerald-200'
                                        }`}>
                                    <FiCopy size={12} /> Copiar
                                </button>
                            )}
                            {!isFullyDone && trackId0 && (
                                <button onClick={() => toggleDone(trackId0)} title="Marcar como enviado"
                                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors">
                                    <FiCheckCircle size={12} /> Enviado
                                </button>
                            )}
                            {/* WA buttons - one per group or legacy single */}
                            {gruposWa.length > 0 ? gruposWa.filter(g => g.enlace).map((g, gi) => (
                                <a key={g.id} href={g.enlace} target="_blank" rel="noopener noreferrer" title={g.nombre || `Grupo ${gi + 1}`}
                                    className="flex items-center gap-1 px-2 py-1.5 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors">
                                    <FiExternalLink size={11} /> {gruposWa.length > 1 ? `G${gi + 1}` : 'WA'}
                                </a>
                            )) : hasWaGroup && (
                                <a href={seg.grupoWhatsapp} target="_blank" rel="noopener noreferrer" title="Abrir grupo de WhatsApp"
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors">
                                    <FiExternalLink size={12} /> WA
                                </a>
                            )}
                            <button onClick={() => setExpandedCourseId(prev => prev === course.id ? null : course.id)} title="Ver fechas y grupos"
                                className={`p-1.5 rounded-lg transition-colors ${expandedCourseId === course.id ? 'text-indigo-600 bg-indigo-50' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`}>
                                <FiArrowRight size={14} className={`transition-transform ${expandedCourseId === course.id ? 'rotate-90' : ''}`} />
                            </button>
                        </div>
                    )}

                    {/* Actions for multi-type: only WA + navigate */}
                    {!singleType && (
                        <div className="flex items-center gap-1.5 shrink-0">
                            {hasImage && (
                                <a href={seg.imagenSeguimiento} target="_blank" rel="noopener noreferrer" title="Ver/descargar poster"
                                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-colors">
                                    <FiImage size={12} />
                                </a>
                            )}
                            {/* WA buttons - one per group or legacy single */}
                            {gruposWa.length > 0 ? gruposWa.filter(g => g.enlace).map((g, gi) => (
                                <a key={g.id} href={g.enlace} target="_blank" rel="noopener noreferrer" title={g.nombre || `Grupo ${gi + 1}`}
                                    className="flex items-center gap-1 px-2 py-1.5 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors">
                                    <FiExternalLink size={11} /> {gruposWa.length > 1 ? `G${gi + 1}` : 'WA'}
                                </a>
                            )) : hasWaGroup && (
                                <a href={seg.grupoWhatsapp} target="_blank" rel="noopener noreferrer" title="Abrir grupo de WhatsApp"
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors">
                                    <FiExternalLink size={12} /> WA
                                </a>
                            )}
                            <button onClick={() => setExpandedCourseId(prev => prev === course.id ? null : course.id)} title="Ver fechas y grupos"
                                className={`p-1.5 rounded-lg transition-colors ${expandedCourseId === course.id ? 'text-indigo-600 bg-indigo-50' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`}>
                                <FiArrowRight size={14} className={`transition-transform ${expandedCourseId === course.id ? 'rotate-90' : ''}`} />
                            </button>
                        </div>
                    )}
                </div>

                {/* Sub-rows for multi-type */}
                {!singleType && enabledTipos.length > 0 && (() => {
                    const recordatorioDone = doneIds.has(`${course.id}::recordatorio`);
                    return (
                        <div className="border-t border-gray-100 px-3 pb-2 pt-1 space-y-1">
                            {enabledTipos.map(tipo => {
                                const trackId = `${course.id}::${tipo.id}`;
                                const isDone = doneIds.has(trackId);
                                const hasPlantilla = !!tipo.plantilla;
                                const showRecommendation = !isDone && recordatorioDone && tipo.id !== 'recordatorio';
                                return (
                                    <div key={trackId} className="flex items-center gap-2 py-1">
                                        <button onClick={() => toggleDone(trackId)}
                                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${isDone
                                                ? 'bg-green-500 border-green-500 text-white'
                                                : 'border-gray-300 hover:border-green-400 text-transparent hover:text-green-300'
                                                }`}>
                                            <FiCheck size={10} strokeWidth={3} />
                                        </button>
                                        <span className={`text-xs font-medium ${isDone ? 'line-through text-gray-400' : 'text-gray-600'}`}>
                                            {tipo.nombre}
                                        </span>
                                        {showRecommendation && (
                                            <span className="flex items-center gap-0.5 text-[9px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full border border-amber-200 whitespace-nowrap">
                                                <FiClock size={8} /> 2h después
                                            </span>
                                        )}
                                        <span className="flex-1" />
                                        {hasPlantilla && (
                                            <button onClick={() => copyTypedMessage(course, tipo)}
                                                className={`flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-md border transition-colors ${isDone
                                                    ? 'text-gray-400 bg-gray-50 border-gray-200'
                                                    : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border-emerald-200'
                                                    }`}>
                                                <FiCopy size={9} /> Copiar
                                            </button>
                                        )}
                                        {!isDone && (
                                            <button onClick={() => toggleDone(trackId)}
                                                className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md border border-blue-200 transition-colors">
                                                Enviado
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })()}

                {/* Selected indicator */}
                {expandedCourseId === course.id && (
                    <div className="border-t border-indigo-200 bg-indigo-50/50 px-3 py-1.5 text-[10px] text-indigo-600 font-medium flex items-center gap-1">
                        <FiChevronDown size={10} /> Panel de configuración abierto a la derecha
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-5">
                <h1 className="text-2xl font-bold text-gray-800">Seguimiento Diario</h1>
                <p className="text-gray-500 text-sm capitalize">
                    {format(new Date(), "EEEE d 'de' MMMM, yyyy", { locale: es })}
                </p>
            </div>

            {loadingTracking && (
                <div className="flex items-center gap-2 mb-4 text-sm text-gray-400">
                    <FiRefreshCw size={14} className="animate-spin" />
                    Cargando progreso...
                </div>
            )}

            {/* Two-column layout */}
            <div className="flex gap-6">
                {/* LEFT COLUMN - Main list */}
                <div className="flex-1 min-w-0 space-y-5">
                    {/* Search */}
                    <div className="relative">
                        <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            placeholder="Buscar curso..."
                            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 bg-gray-50"
                        />
                    </div>

                    {/* Progress bar */}
                    {allGroups.length > 0 && (
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="text-xs font-bold text-gray-500">
                                    Progreso: {totalDoneItems} de {totalTrackable} mensajes
                                </span>
                                <div className="flex items-center gap-2">
                                    <span className={`text-xs font-bold ${progress === 100 ? 'text-green-600' : 'text-gray-400'}`}>
                                        {progress}%
                                    </span>
                                    {totalDoneItems > 0 && (
                                        <button
                                            onClick={resetAll}
                                            title="Reiniciar progreso"
                                            className="text-gray-400 hover:text-red-500 transition-colors"
                                        >
                                            <FiRefreshCw size={12} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all duration-500 ${progress === 100 ? 'bg-green-500' : 'bg-indigo-500'}`}
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* All done celebration */}
                    {allGroups.length > 0 && progress === 100 && (
                        <div className="text-center py-6 bg-green-50 border border-green-200 rounded-2xl">
                            <FiCheckCircle size={40} className="mx-auto mb-2 text-green-500" />
                            <p className="text-lg font-bold text-green-700">Seguimiento completo</p>
                            <p className="text-sm text-green-600">Todos los mensajes enviados hoy</p>
                        </div>
                    )}

                    {/* Pending groups */}
                    {pending.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                                <h2 className="text-sm font-bold text-gray-600 uppercase tracking-wide">
                                    Pendientes ({pending.length})
                                </h2>
                            </div>
                            <div className="space-y-2">
                                {pending.map(course => (
                                    <GroupRow key={course.id} course={course} isFullyDone={false} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Done groups */}
                    {done.length > 0 && progress < 100 && (
                        <div>
                            <h2 className="text-sm font-bold text-green-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                                <FiCheckCircle size={14} /> Completados ({done.length})
                            </h2>
                            <div className="space-y-2">
                                {done.map(course => (
                                    <GroupRow key={course.id} course={course} isFullyDone={true} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Empty states */}
                    {courses.length === 0 && (
                        <div className="text-center py-16 text-gray-400">
                            <FiCalendar size={48} className="mx-auto mb-4 opacity-50" />
                            <p className="text-lg font-medium">No hay cursos creados</p>
                            <p className="text-sm">Crea un curso desde la barra lateral para comenzar</p>
                        </div>
                    )}

                    {courses.length > 0 && allGroups.length === 0 && (
                        <div className="text-center py-16 text-gray-400">
                            <FiCheckCircle size={48} className="mx-auto mb-4 text-green-400" />
                            <p className="text-lg font-medium text-gray-600">Todo al dia</p>
                            <p className="text-sm">Todos los cursos estan inactivos.</p>
                        </div>
                    )}
                </div>

                {/* RIGHT COLUMN - Config Editor OR Stats */}
                <div className="w-[420px] shrink-0 hidden lg:block">
                    <div className="sticky top-24 space-y-4">

                    {/* Inline Seguimiento Editor */}
                    {expandedCourseId && (() => {
                        const selectedCourse = allGroups.find(c => c.id === expandedCourseId) || courses.find(c => c.id === expandedCourseId);
                        if (!selectedCourse) return null;
                        return (
                            <div className="bg-white border border-indigo-200 rounded-xl p-4 shadow-sm max-h-[calc(100vh-120px)] overflow-y-auto">
                                <SeguimientoInlineEditor
                                    course={selectedCourse}
                                    onNavigateConfig={() => navigate(`/generador/curso/${selectedCourse.id}?tab=config`)}
                                    onClose={() => setExpandedCourseId(null)}
                                />
                            </div>
                        );
                    })()}

                    {/* Stats (show when no course selected) */}
                    {!expandedCourseId && allGroups.length > 0 && (<>
                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-2">
                                <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-2.5 text-center">
                                    <p className="text-xl font-bold text-indigo-600">{stats.total}</p>
                                    <p className="text-[10px] text-indigo-500 font-medium">Activos</p>
                                </div>
                                <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-center">
                                    <p className="text-xl font-bold text-red-600">{stats.hoy}</p>
                                    <p className="text-[10px] text-red-500 font-medium">Hoy</p>
                                </div>
                                <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 text-center">
                                    <p className="text-xl font-bold text-amber-600">{stats.proximos3}</p>
                                    <p className="text-[10px] text-amber-500 font-medium">Prox. 3d</p>
                                </div>
                            </div>
                            {inactivosCount > 0 && (
                                <div className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-center">
                                    <span className="text-sm font-bold text-gray-400">{inactivosCount}</span>
                                    <span className="text-[10px] text-gray-400 font-medium ml-1">Inactivos</span>
                                </div>
                            )}

                            {/* History: Weekly / Monthly toggle */}
                            <div className="bg-white border border-gray-200 rounded-xl p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-[10px] font-bold text-gray-500 uppercase">Historial</h3>
                                    <div className="flex bg-gray-100 rounded-lg p-0.5">
                                        <button
                                            onClick={() => setHistoryView('semanal')}
                                            className={`text-[10px] font-bold px-2.5 py-1 rounded-md transition-colors ${historyView === 'semanal' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                                        >
                                            Semanal
                                        </button>
                                        <button
                                            onClick={() => setHistoryView('mensual')}
                                            className={`text-[10px] font-bold px-2.5 py-1 rounded-md transition-colors ${historyView === 'mensual' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                                        >
                                            Mensual
                                        </button>
                                    </div>
                                </div>

                                {historyView === 'semanal' ? (() => {
                                    const week = getWeekHistory(totalTrackable, weekData);
                                    return (
                                        <>
                                            <div className="flex items-end gap-1.5 justify-between">
                                                {week.map((d, i) => (
                                                    <div
                                                        key={i}
                                                        className={`flex flex-col items-center gap-1 flex-1 ${!d.isFuture ? 'cursor-pointer hover:opacity-80' : ''}`}
                                                        onClick={() => !d.isFuture && setSelectedDay(d)}
                                                    >
                                                        <span className={`text-[9px] font-bold ${d.count > 0 && !d.isFuture ? 'text-green-600' : 'text-gray-400'}`}>
                                                            {d.isFuture ? '' : d.count > 0 ? `${d.pct}%` : '0'}
                                                        </span>
                                                        <div className={`w-full h-20 bg-gray-100 rounded-md overflow-hidden flex flex-col justify-end ${!d.isFuture ? 'hover:ring-2 hover:ring-indigo-300 transition-all' : ''}`}>
                                                            {!d.isFuture && d.pct > 0 && (
                                                                <div
                                                                    className={`w-full rounded-md transition-all ${d.pct === 100 ? 'bg-green-500' : 'bg-indigo-400'}`}
                                                                    style={{ height: `${Math.max(d.pct, 8)}%` }}
                                                                />
                                                            )}
                                                        </div>
                                                        <span className={`text-[9px] font-bold ${d.isToday ? 'text-indigo-600' : 'text-gray-400'}`}>{d.label}</span>
                                                        <span className={`text-[9px] ${d.isToday ? 'bg-indigo-600 text-white rounded-full w-4 h-4 flex items-center justify-center font-bold' : 'text-gray-400'}`}>{d.date}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            <p className="text-[9px] text-gray-400 mt-2 text-center">Clic en un día para ver detalle</p>
                                        </>
                                    );
                                })() : (() => {
                                    const today = new Date();
                                    const year = today.getFullYear();
                                    const month = today.getMonth();
                                    const daysInMonth = new Date(year, month + 1, 0).getDate();
                                    const firstDayOfWeek = new Date(year, month, 1).getDay();
                                    const offset = (firstDayOfWeek + 6) % 7; // Monday = 0
                                    const dayHeaders = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

                                    const cells = [];
                                    for (let i = 0; i < offset; i++) cells.push(null);
                                    for (let d = 1; d <= daysInMonth; d++) {
                                        const dateStr = format(new Date(year, month, d), 'yyyy-MM-dd');
                                        const count = (monthData[dateStr] || []).length;
                                        const isToday = dateStr === todayKey;
                                        const isFuture = new Date(year, month, d) > today;
                                        cells.push({
                                            day: d,
                                            dateKey: dateStr,
                                            count,
                                            isToday,
                                            isFuture,
                                            pct: totalTrackable > 0 ? Math.round((count / totalTrackable) * 100) : 0,
                                            fullLabel: format(new Date(year, month, d), "EEEE d 'de' MMMM", { locale: es })
                                        });
                                    }

                                    // Total messages this month
                                    const totalMonthMessages = Object.values(monthData).reduce((acc, ids) => acc + ids.length, 0);
                                    const daysWithActivity = Object.values(monthData).filter(ids => ids.length > 0).length;

                                    return (
                                        <>
                                            <p className="text-xs font-semibold text-gray-600 capitalize text-center mb-2">
                                                {format(today, "MMMM yyyy", { locale: es })}
                                            </p>
                                            {/* Day of week headers */}
                                            <div className="grid grid-cols-7 gap-1 mb-1">
                                                {dayHeaders.map((h, i) => (
                                                    <div key={i} className="text-[9px] font-bold text-gray-400 text-center">{h}</div>
                                                ))}
                                            </div>
                                            {/* Calendar grid */}
                                            <div className="grid grid-cols-7 gap-1">
                                                {cells.map((cell, i) => {
                                                    if (!cell) return <div key={`empty-${i}`} />;
                                                    const bg = cell.isFuture
                                                        ? 'bg-gray-50 text-gray-300'
                                                        : cell.pct === 100
                                                            ? 'bg-green-500 text-white font-bold'
                                                            : cell.pct > 0
                                                                ? 'bg-indigo-100 text-indigo-700 font-semibold'
                                                                : 'bg-gray-100 text-gray-400';
                                                    return (
                                                        <div
                                                            key={cell.dateKey}
                                                            className={`relative aspect-square rounded-md flex items-center justify-center text-[10px] transition-all ${bg} ${!cell.isFuture ? 'cursor-pointer hover:ring-2 hover:ring-indigo-300' : ''} ${cell.isToday ? 'ring-2 ring-indigo-500' : ''}`}
                                                            onClick={() => !cell.isFuture && setSelectedDay(cell)}
                                                            title={`${cell.fullLabel}: ${cell.count} mensajes (${cell.pct}%)`}
                                                        >
                                                            {cell.day}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {/* Monthly summary */}
                                            <div className="flex items-center justify-between mt-3 text-[9px] text-gray-400">
                                                <span>{totalMonthMessages} mensajes enviados</span>
                                                <span>{daysWithActivity} días con actividad</span>
                                            </div>
                                            {/* Legend */}
                                            <div className="flex items-center justify-center gap-3 mt-2">
                                                <div className="flex items-center gap-1">
                                                    <div className="w-2.5 h-2.5 rounded-sm bg-gray-100" />
                                                    <span className="text-[8px] text-gray-400">0%</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <div className="w-2.5 h-2.5 rounded-sm bg-indigo-100" />
                                                    <span className="text-[8px] text-gray-400">Parcial</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <div className="w-2.5 h-2.5 rounded-sm bg-green-500" />
                                                    <span className="text-[8px] text-gray-400">100%</span>
                                                </div>
                                            </div>
                                            <p className="text-[9px] text-gray-400 mt-2 text-center">Clic en un día para ver detalle</p>
                                        </>
                                    );
                                })()}
                            </div>

                            {/* Instructions */}
                            {progress < 100 && (
                                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-[10px] text-blue-700 space-y-1">
                                    <p className="font-bold text-xs">Flujo:</p>
                                    <ol className="list-decimal list-inside space-y-0.5">
                                        <li><strong>Copiar</strong> — Copia el mensaje</li>
                                        <li><strong>WA</strong> — Abre el grupo y pega</li>
                                        <li><strong>Enviado</strong> — Marca como hecho</li>
                                    </ol>
                                </div>
                            )}
                    </>)}

                    </div>
                </div>
            </div>

            {/* Day Detail Popup */}
            {selectedDay && (() => {
                const dayDoneRaw = weekData[selectedDay.dateKey] || [];
                const dayDoneIds = new Set(normalizeDoneIds(dayDoneRaw));
                const activos = courses.filter(c => (c.seguimiento?.estado || 'activo') !== 'inactivo');
                const completados = activos.filter(c => {
                    const tipos = normalizeTiposMensaje(c.seguimiento || {}).filter(t => t.habilitado);
                    return tipos.length > 0 && tipos.every(t => dayDoneIds.has(`${c.id}::${t.id}`));
                });
                const sinEnviar = activos.filter(c => {
                    const tipos = normalizeTiposMensaje(c.seguimiento || {}).filter(t => t.habilitado);
                    return tipos.length === 0 || tipos.some(t => !dayDoneIds.has(`${c.id}::${t.id}`));
                });

                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setSelectedDay(null)}>
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center justify-between p-4 border-b border-gray-100">
                                <div>
                                    <h3 className="font-bold text-gray-800 capitalize">{selectedDay.fullLabel}</h3>
                                    <p className="text-sm text-gray-500">
                                        {completados.length} de {activos.length} cursos completos — {dayDoneRaw.length} mensajes enviados
                                    </p>
                                </div>
                                <button onClick={() => setSelectedDay(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                    <FiX size={18} className="text-gray-500" />
                                </button>
                            </div>
                            <div className="p-4 overflow-y-auto max-h-[60vh] space-y-4">
                                {completados.length > 0 && (
                                    <div>
                                        <h4 className="text-xs font-bold text-green-600 uppercase mb-2 flex items-center gap-1.5">
                                            <FiCheckCircle size={12} /> Completados ({completados.length})
                                        </h4>
                                        <div className="space-y-1.5">
                                            {completados.map(c => (
                                                <div key={c.id} className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-100 rounded-lg">
                                                    <FiCheck size={14} className="text-green-500 shrink-0" />
                                                    <span className="text-sm text-gray-700 truncate">{c.title}</span>
                                                    {c.seguimiento?.nombreGrupo && (
                                                        <span className="text-[10px] text-green-600 truncate ml-auto">{c.seguimiento.nombreGrupo}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {sinEnviar.length > 0 && (
                                    <div>
                                        <h4 className="text-xs font-bold text-red-500 uppercase mb-2 flex items-center gap-1.5">
                                            <FiX size={12} /> Pendientes ({sinEnviar.length})
                                        </h4>
                                        <div className="space-y-1.5">
                                            {sinEnviar.map(c => (
                                                <div key={c.id} className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg">
                                                    <div className="w-3.5 h-3.5 rounded-full border-2 border-red-300 shrink-0" />
                                                    <span className="text-sm text-gray-700 truncate">{c.title}</span>
                                                    {c.seguimiento?.nombreGrupo && (
                                                        <span className="text-[10px] text-gray-400 truncate ml-auto">{c.seguimiento.nombreGrupo}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {completados.length === 0 && sinEnviar.length === 0 && (
                                    <p className="text-center text-gray-400 py-8 text-sm">No hay datos para este día</p>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default SeguimientoDiario;
